import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PageHero from '../PageHero/PageHero';
import Footer from '../Footer/Footer';
import SendIcon from '@mui/icons-material/Send';

const RegistrationSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  }
}));

const RegistrationForm = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  maxWidth: '600px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: '24px'
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '&:hover fieldset': {
      borderColor: '#85b47c'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#455b40'
    }
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: '#455b40'
    }
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  padding: '12px 32px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#6a8f61',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(133, 180, 124, 0.3)'
  },
  '&:disabled': {
    backgroundColor: '#cccccc'
  }
}));

interface FormData {
  name: string;
  email: string;
  number: string;
}

const CourseRegistration: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    number: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const formDataToSend = new FormData();
    formDataToSend.append("_wpcf7", "347");
    formDataToSend.append("_wpcf7_version", "5.9.6");
    formDataToSend.append("_wpcf7_locale", "es_ES");
    formDataToSend.append("_wpcf7_unit_tag", "curso-registro");
    formDataToSend.append("_wpcf7_container_post", "0");
    formDataToSend.append("your-name", formData.name);
    formDataToSend.append("your-email", formData.email);
    formDataToSend.append("your-number", formData.number);

    try {
      const res = await fetch(
        "https://thomass362.sg-host.com/wp-json/contact-form-7/v1/contact-forms/347/feedback",
        {
          method: "POST",
          body: formDataToSend
        }
      );

      const data = await res.json();
      
      if (data.status === 'mail_sent') {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          number: ''
        });
      } else {
        setError(data.message || 'Error al enviar el formulario. Por favor, intente nuevamente.');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero 
        title="Registro de Curso"
        subtitle="Apúntate a nuestros cursos"
        description="Completa el formulario y nos pondremos en contacto contigo para finalizar tu registro."
        backgroundImage="https://images.unsplash.com/photo-1544947950-fa07a98d237f?q=80&w=2074"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Registro de Curso', link: '/registro-curso' }
        ]}
        overlay={true}
        overlayOpacity={0.5}
      />

      <RegistrationSection>
        <Container maxWidth="lg">
          <RegistrationForm>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: '"Lora", serif',
                fontWeight: 600,
                mb: 3,
                color: '#2a3a28',
                textAlign: 'center'
              }}
            >
              Formulario de Registro
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                textAlign: 'center',
                mb: 4
              }}
            >
              Proporciona tus datos de contacto y nos pondremos en contacto contigo para completar tu registro en el curso.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                ¡Registro enviado exitosamente! Nos pondremos en contacto contigo pronto.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <StyledTextField
                  fullWidth
                  label="Nombre completo"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />

                <StyledTextField
                  fullWidth
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />

                <StyledTextField
                  fullWidth
                  label="Número de teléfono"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <SubmitButton
                    type="submit"
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  >
                    {loading ? 'Enviando...' : 'Registrarse al Curso'}
                  </SubmitButton>
                </Box>
              </Stack>
            </form>
          </RegistrationForm>
        </Container>
      </RegistrationSection>

      <Footer />
    </>
  );
};

export default CourseRegistration;